import { useState, useEffect } from 'react';
import { fetchWithCache } from '../../utils/apiCache';
import { useParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';
import ToolsSection from '../../components/feature/ToolsSection';
import RequestQuotePopup from '../../components/feature/RequestQuotePopup';
import { type PdfProject, type VideoProject, type Rendering360Project, type ViewDirection } from '../../mocks/portfolio';

const viewLabels: { key: ViewDirection; label: string; icon: string }[] = [
  { key: "south", label: "South View", icon: "ri-arrow-down-line" },
  { key: "north", label: "North View", icon: "ri-arrow-up-line" },
  { key: "west", label: "West View", icon: "ri-arrow-left-line" },
  { key: "east", label: "East View", icon: "ri-arrow-right-line" },
];

export default function ServiceDetailPage() {
  const { serviceName } = useParams();
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [selectedPdf, setSelectedPdf] = useState<PdfProject | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoProject | null>(null);
  const [selected360, setSelected360] = useState<Rendering360Project | null>(null);
  const [active360Tab, setActive360Tab] = useState<ViewDirection>("south");

  const slugify = (text: string) => text.toLowerCase().replace(/\s+/g, '-').replace(/[&/]/g, '');

  const [currentService, setCurrentService] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const defaultService = {
    title: 'Service Not Found',
    subtitle: '',
    heroImage: 'https://images.unsplash.com/photo-1503387762-592dea58ef21?q=80&w=2000&auto=format&fit=crop',
    description: {
      what: 'The requested service could not be found. Please return to the services page to explore our offerings.',
      who: '',
      why: ''
    },
    valueProps: [],
    portfolioProjects: [],
    caseStudy: null
  };

  useEffect(() => {
    const fetchServiceData = async () => {
      try {
        setLoading(true);
        // 1. Fetch all CMS data to find the current service by slug
        const cmsData = await fetchWithCache("https://partner.designsbydmi.com/api/cms/all");

        const allServices = [...(cmsData.draftingServices || []), ...(cmsData.takeoffServices || [])];
        const foundService = allServices.find((s: any) => slugify(s.title) === serviceName);

        if (foundService) {
          // 2. Fetch specific portfolio items for this exact service
          const portfolioData = await fetchWithCache(`https://partner.designsbydmi.com/api/cms/portfolio/service/${encodeURIComponent(foundService.title)}`);

          setCurrentService({
            title: foundService.title,
            subtitle: foundService.tags?.join(' • ') || '',
            heroImage: foundService.image,
            description: {
              what: foundService.description,
              who: 'Ideal for professionals needing accurate documentation and design validation.',
              why: 'Accuracy and clarity in design documentation prevent conflicts, ensure compliance, and streamline the construction process.'
            },
            valueProps: [
              { icon: foundService.icon || 'ri-service-line', title: 'Expert Delivery', description: 'Precision measurements and documentation.' }
            ],
            portfolioProjects: portfolioData || [],
            caseStudy: null
          });
        } else {
          setCurrentService(defaultService);
        }
      } catch (err) {
        console.error("Failed to load service", err);
        setCurrentService(defaultService);
      } finally {
        setLoading(false);
      }
    };

    if (serviceName) {
      fetchServiceData();
    }
  }, [serviceName]);

  // SEO Update
  useEffect(() => {
    if (currentService) {
      document.title = `${currentService.title} | Drafting by DMI`;

      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', currentService.subtitle || currentService.description.what.substring(0, 160));
    }

    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, [currentService]);

  const open360 = (project: Rendering360Project) => {
    setActive360Tab("south");
    setSelected360(project);
  };

  const closeAllModals = () => {
    setSelectedProject(null);
    setSelectedPdf(null);
    setSelectedVideo(null);
    setSelected360(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="ri-loader-4-line text-4xl text-[#e18033] animate-spin"></i>
          <p className="text-gray-500 font-medium">Loading service details...</p>
        </div>
      </div>
    );
  }

  if (!currentService) return null;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-[#dbcfbf]/30 to-white dark:from-slate-800/50 dark:to-slate-900 transition-colors duration-300">
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage: `url('${currentService.heroImage}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#212026] dark:text-gray-100 mb-6 transition-colors duration-300">
              {currentService.title}
            </h1>
            <p className="text-xl md:text-2xl text-[#889d8f] dark:text-gray-300 max-w-4xl mx-auto transition-colors duration-300">
              {currentService.subtitle}
            </p>
          </div>
        </section>

        {/* Service Description Section */}
        <section className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-8 transition-colors duration-300">
                  What We Deliver
                </h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-[#e18033] mb-3">What it is</h3>
                    <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                      {currentService.description.what}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#e18033] mb-3">Who benefits</h3>
                    <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                      {currentService.description.who}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#e18033] mb-3">Why it matters</h3>
                    <p className="text-[#889d8f] dark:text-gray-300 leading-relaxed transition-colors duration-300">
                      {currentService.description.why}
                    </p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  src={currentService.heroImage}
                  alt="Service Description"
                  className="w-full h-96 object-cover object-top rounded-2xl shadow-xl dark:shadow-slate-900/50"
                />
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#e18033] rounded-2xl flex items-center justify-center shadow-xl dark:shadow-slate-900/50">
                  <div className="text-center text-white">
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                      <i className="ri-award-line text-2xl"></i>
                    </div>
                    <div className="text-sm font-semibold">Expert</div>
                    <div className="text-sm">Service</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio Showcase Section */}
        <section className="py-20 bg-white dark:bg-slate-900 transition-colors duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#212026] dark:text-gray-100 mb-4 transition-colors duration-300">
                Portfolio Showcase
              </h2>
              <p className="text-xl text-[#889d8f] dark:text-gray-300 max-w-3xl mx-auto transition-colors duration-300">
                See our expertise in action through successful project examples
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {currentService.portfolioProjects.map((project: any) => {
                // ── PDF Card ──────────────────────────────────────────────
                if (project.type === "pdf") {
                  return (
                    <Card
                      key={project.id}
                      className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300 flex flex-col h-full"
                      onClick={() => setSelectedPdf(project)}
                    >
                      <div className="relative flex-1 min-h-[16rem] w-full bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                        {project.thumbnail || project.image ? (
                          <img
                            src={project.thumbnail || project.image}
                            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                            alt={project.title}
                          />
                        ) : (
                          <div className="w-full h-full overflow-hidden group-hover:scale-110 transition-transform duration-500 relative bg-white">
                            <div className="absolute inset-0 z-10" />
                            <iframe
                              src={`${project.pdfUrl || project.url}#page=1&view=FitH&scrollbar=0&toolbar=0&navpanes=0`}
                              className="w-[150%] h-[150%] -ml-[25%] -mt-[25%] pointer-events-none"
                              style={{ border: 'none' }}
                              title={project.title}
                            />
                          </div>
                        )}
                        {/* Dark overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        {/* PDF Badge */}
                        <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full shadow-lg">
                          <i className="ri-file-pdf-line text-sm" />
                          PDF
                        </div>
                        {/* Category badge */}
                        <div className="absolute top-4 right-4 px-2 py-1 bg-[#212026]/70 text-white text-xs rounded-full">
                          {project.category}
                        </div>
                        {/* Hover CTA */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="text-center text-white">
                            <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-2">
                              <i className="ri-file-pdf-line text-xl" />
                            </div>
                            <p className="text-sm font-medium">Open PDF</p>
                          </div>
                        </div>
                      </div>

                    </Card>
                  );
                }

                // ── 360° Rendering Card ───────────────────────────────────
                if (project.type === "rendering360") {
                  return (
                    <Card
                      key={project.id}
                      className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300 flex flex-col h-full"
                      onClick={() => open360(project)}
                    >
                      <div className="relative flex-1 min-h-[16rem] w-full bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                        <img
                          src={project.image}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                          alt={project.title}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        {/* 360° Badge */}
                        <div className="absolute top-4 right-4 flex items-center gap-1 px-3 py-1.5 bg-[#e18033] text-white text-xs font-extrabold rounded-full shadow-lg tracking-wider">
                          <i className="ri-360-line text-sm" />
                          360°
                        </div>
                        {/* Category */}
                        <div className="absolute top-4 left-4 px-3 py-1 bg-[#212026]/70 text-white text-xs font-medium rounded-full">
                          {project.category}
                        </div>
                        {/* Hover CTA */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="text-center text-white">
                            <div className="w-12 h-12 bg-[#e18033] rounded-full flex items-center justify-center mx-auto mb-2">
                              <i className="ri-360-line text-xl" />
                            </div>
                            <p className="text-sm font-medium">View All Angles</p>
                          </div>
                        </div>
                      </div>

                    </Card>
                  );
                }

                // ── Video Card ────────────────────────────────────────────
                if (project.type === "video") {
                  return (
                    <Card
                      key={project.id}
                      className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300 flex flex-col h-full"
                      onClick={() => setSelectedVideo(project)}
                    >
                      <div className="relative flex-1 min-h-[16rem] w-full bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                        {project.thumbnail || project.image ? (
                          <img
                            src={project.thumbnail || project.image}
                            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                            alt={project.title}
                          />
                        ) : (
                          <video
                            src={`${project.videoUrl || project.url}#t=0.1`}
                            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                            preload="metadata"
                            muted
                            playsInline
                          />
                        )}
                        {/* Dark overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        {/* Video Badge */}
                        <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 bg-violet-600 text-white text-xs font-bold rounded-full shadow-lg">
                          <i className="ri-play-circle-line text-sm" />
                          VIDEO
                        </div>
                        {/* Category badge */}
                        <div className="absolute top-4 right-4 px-2 py-1 bg-[#212026]/70 text-white text-xs rounded-full">
                          {project.category}
                        </div>
                        {/* Play button hover */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="text-center text-white">
                            <div className="w-14 h-14 bg-violet-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-xl">
                              <i className="ri-play-fill text-2xl ml-0.5" />
                            </div>
                            <p className="text-sm font-medium">Play Video</p>
                          </div>
                        </div>
                      </div>

                    </Card>
                  );
                }

                // ── Standard Image Card ───────────────────────────────────
                return (
                  <Card
                    key={project.id}
                    className="group overflow-hidden cursor-pointer bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 transition-colors duration-300"
                    onClick={() =>
                      setSelectedProject(
                        selectedProject === project.id ? null : project.id
                      )
                    }
                  >
                    <div className="relative h-64 bg-[#dbcfbf]/10 dark:bg-slate-700/30">
                      <img
                        src={project.image}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#212026]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="text-center text-white">
                          <div className="w-12 h-12 bg-[#e18033] rounded-full flex items-center justify-center mx-auto mb-2">
                            <i className="ri-eye-line text-xl" />
                          </div>
                          <p className="text-sm font-medium">View Project</p>
                        </div>
                      </div>
                      <div className="absolute top-4 left-4 px-3 py-1 bg-[#e18033] text-white text-xs font-medium rounded-full">
                        {project.category}
                      </div>
                      {/* Disclaimer badge with tooltip */}
                      {project.disclaimer && (
                        <div className="absolute bottom-3 right-3 group/disclaimer z-10" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/90 text-white text-[11px] font-semibold rounded-full cursor-help shadow-md backdrop-blur-sm">
                            <i className="ri-error-warning-line text-xs" />
                            {project.disclaimer.label}
                          </div>
                          {/* Tooltip */}
                          <div className="absolute bottom-full right-0 mb-2 w-60 px-3 py-2.5 bg-[#212026] text-white text-xs leading-relaxed rounded-xl shadow-xl opacity-0 group-hover/disclaimer:opacity-100 pointer-events-none transition-opacity duration-200 z-20">
                            {project.disclaimer.description}
                            <div className="absolute top-full right-4 w-2.5 h-2.5 bg-[#212026] rotate-45 -mt-1.5" />
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Image modal */}
                    {selectedProject === project.id && (
                      <div
                        className="fixed inset-0 bg-[#212026]/80 dark:bg-black/90 flex items-center justify-center z-50 p-4"
                        onClick={closeAllModals}
                      >
                        <div
                          className="bg-white dark:bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="relative">
                            <img
                              src={project.image}
                              className="w-full h-full object-cover object-top rounded-t-2xl"
                              alt="Project"
                            />
                            <button
                              onClick={closeAllModals}
                              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-[#e18033]/80 rounded-full flex items-center justify-center cursor-pointer transition-colors duration-300"
                            >
                              <i className="ri-close-line text-white text-xl" />
                            </button>
                            <div className="absolute top-4 left-4 px-3 py-1 bg-[#e18033] text-white text-sm font-medium rounded-full">
                              {project.category}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Video Popup Modal ─────────────────────────────────────────────── */}
        {selectedVideo && (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedVideo(null)}
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-4xl flex flex-col overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-violet-600 rounded-lg flex items-center justify-center">
                    <i className="ri-play-circle-line text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#212026] dark:text-gray-100 text-base">
                      {selectedVideo.title}
                    </h3>
                    <p className="text-xs text-[#889d8f] dark:text-gray-400">
                      {selectedVideo.category}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={selectedVideo.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#e18033] text-white text-sm font-medium rounded-lg hover:bg-[#c96a1a] transition-colors flex items-center gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-external-link-line" />
                    Open in New Tab
                  </a>
                  <button
                    onClick={() => setSelectedVideo(null)}
                    className="w-9 h-9 bg-gray-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                  >
                    <i className="ri-close-line text-[#212026] dark:text-gray-200 text-lg" />
                  </button>
                </div>
              </div>
              {/* Video Player */}
              <div className="bg-black">
                <video
                  key={selectedVideo.videoUrl}
                  src={selectedVideo.videoUrl}
                  controls
                  autoPlay
                  className="w-full max-h-[70vh] outline-none"
                  style={{ display: "block" }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        )}

        {/* ── PDF Popup Modal ───────────────────────────────────────────────── */}
        {selectedPdf && (
          <div
            className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedPdf(null)}
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-red-600 rounded-lg flex items-center justify-center">
                    <i className="ri-file-pdf-line text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#212026] dark:text-gray-100 text-base">
                      {selectedPdf.title}
                    </h3>
                    <p className="text-xs text-[#889d8f] dark:text-gray-400">
                      {selectedPdf.category}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={selectedPdf.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#e18033] text-white text-sm font-medium rounded-lg hover:bg-[#c96a1a] transition-colors flex items-center gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-external-link-line" />
                    Open in New Tab
                  </a>
                  <button
                    onClick={() => setSelectedPdf(null)}
                    className="w-9 h-9 bg-gray-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                  >
                    <i className="ri-close-line text-[#212026] dark:text-gray-200 text-lg" />
                  </button>
                </div>
              </div>
              {/* PDF iframe */}
              <div className="flex-1 bg-gray-100 dark:bg-slate-900">
                <iframe
                  src={selectedPdf.pdfUrl}
                  className="w-full h-full border-0"
                  title={selectedPdf.title}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── 360° Rendering Popup Modal ────────────────────────────────────── */}
        {selected360 && (
          <div
            className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4"
            onClick={() => setSelected360(null)}
          >
            <div
              className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#e18033] rounded-lg flex items-center justify-center">
                    <i className="ri-360-line text-white text-lg" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#212026] dark:text-gray-100 text-base">
                      {selected360.title}
                    </h3>
                    <p className="text-xs text-[#889d8f] dark:text-gray-400">
                      360° Rendering — All Directional Views
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelected360(null)}
                  className="w-9 h-9 bg-gray-100 dark:bg-slate-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-[#212026] dark:text-gray-200 text-lg" />
                </button>
              </div>

              {/* Tab Bar */}
              <div className="flex border-b border-gray-200 dark:border-slate-700 flex-shrink-0 bg-gray-50 dark:bg-slate-900/50">
                {viewLabels.map((v) => (
                  <button
                    key={v.key}
                    onClick={() => setActive360Tab(v.key)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition-all duration-200 cursor-pointer border-b-2 ${active360Tab === v.key
                      ? "border-[#e18033] text-[#e18033] bg-white dark:bg-slate-800"
                      : "border-transparent text-[#889d8f] dark:text-gray-400 hover:text-[#212026] dark:hover:text-gray-200 hover:bg-white/60 dark:hover:bg-slate-700/40"
                      }`}
                  >
                    <i className={`${v.icon} text-base`} />
                    <span className="hidden sm:inline">{v.label}</span>
                    <span className="sm:hidden capitalize">{v.key}</span>
                  </button>
                ))}
              </div>

              {/* Rendering Image */}
              <div className="flex-1 overflow-hidden bg-[#212026] relative min-h-0">
                <img
                  key={active360Tab}
                  src={selected360.views[active360Tab]}
                  alt={`${active360Tab} view`}
                  className="w-full h-full object-contain animate-fade-in"
                  style={{ animation: "fadeIn 0.3s ease-in-out" }}
                />
                {/* Direction label overlay */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/60 text-white text-sm font-semibold rounded-full backdrop-blur-sm flex items-center gap-2">
                  <i className={`${viewLabels.find((v) => v.key === active360Tab)?.icon} text-[#e18033]`} />
                  {viewLabels.find((v) => v.key === active360Tab)?.label}
                </div>
              </div>
            </div>
          </div>
        )}

        {(() => {
          const selectedProj = currentService.portfolioProjects.find((p: any) => p.id === selectedProject);
          const projectImage = selectedProj?.type === 'image' ? selectedProj.image : (selectedProj?.type === 'rendering360' ? selectedProj.image : null);

          if (!projectImage) return null;

          return (
            <div
              className="fixed inset-0 bg-[#212026]/80 dark:bg-black/90 flex items-center justify-center z-50 p-4"
              onClick={closeAllModals}
            >
              <div
                className="bg-white dark:bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative">
                  <img
                    src={projectImage}
                    className="w-full h-full object-cover object-top rounded-t-2xl"
                    alt="Project"
                  />
                  <button
                    onClick={closeAllModals}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-[#e18033]/80 rounded-full flex items-center justify-center cursor-pointer transition-colors duration-300"
                  >
                    <i className="ri-close-line text-white text-xl" />
                  </button>
                  <div className="absolute top-4 left-4 px-3 py-1 bg-[#e18033] text-white text-sm font-medium rounded-full">
                    {selectedProj?.category}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        <ToolsSection />
        {/* Call-to-Action Section */}
        <ServiceCTA currentService={currentService} />
      </main>
      <Footer />
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to   { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
}

function ServiceCTA({ currentService }: { currentService: any }) {
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);

  return (
    <section className="py-20 bg-[#e18033] dark:bg-[#d16f29] transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
          Ready to get started with {currentService.title.replace(' Solutions', '')}?
        </h2>
        <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
          Let our expert team deliver precision {currentService.title.replace(' Solutions', '').toLowerCase()} services tailored to your project needs
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => setIsQuoteOpen(true)}
            variant="outline"
            size="lg"
            className="!border-white !text-white !hover:bg-white !hover:text-[#e18033] dark:!border-gray-300 dark:!hover:bg-gray-300 dark:!hover:text-slate-900 transition-all duration-300"
          >
            <div className="w-5 h-5 flex items-center justify-center mr-2">
              <i className="ri-phone-line"></i>
            </div>
            Request a Quote
          </Button>
        </div>
      </div>

      <RequestQuotePopup
        isOpen={isQuoteOpen}
        onClose={() => setIsQuoteOpen(false)}
        serviceName={currentService.title}
      />
    </section>
  );
}